import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, arrayUnion } from 'firebase/firestore';
import { db } from './firebase';

const GodModeCalendar = ({ tenantId }) => {
  const [availableYears, setAvailableYears] = useState([]);
  const [academicYear, setAcademicYear] = useState('');
  const [newYearInput, setNewYearInput] = useState('');
  const [isAddingYear, setIsAddingYear] = useState(false);
  
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedHoliday, setSelectedHoliday] = useState('');
  const [holidays, setHolidays] = useState([]);
  
  // --- NEW: SPRINT STATE ---
  const [sprints, setSprints] = useState([]);
  const [activeSprint, setActiveSprint] = useState('');
  const [sprintNum, setSprintNum] = useState(1);
  const [sprintStart, setSprintStart] = useState('');
  const [sprintEnd, setSprintEnd] = useState('');

  const [saveStatus, setSaveStatus] = useState('');
  const [loading, setLoading] = useState(false);

  // 1. Fetch Master District Data & Sprints
  useEffect(() => {
    const fetchMasterData = async () => {
      if (!tenantId) return;
      
      setLoading(true);
      try {
        // Fetch Holidays & Years
        const districtRef = doc(db, 'districts', tenantId);
        const districtSnap = await getDoc(districtRef);
        
        if (districtSnap.exists()) {
          const data = districtSnap.data();
          setHolidays(data.nonInstructionalDays || []);
          
          const years = data.configuredYears || ['2026-2027'];
          setAvailableYears(years);
          if (!academicYear) setAcademicYear(years[0]);
        }

        // Fetch Sprint Data
        const calRef = doc(db, 'districts', tenantId, 'settings', 'calendar');
        const calSnap = await getDoc(calRef);
        if (calSnap.exists()) {
          const calData = calSnap.data();
          setSprints(calData.sprints || []);
          setActiveSprint(calData.activeSprint || '');
          if (calData.sprints) setSprintNum(calData.sprints.length + 1);
        }

      } catch (error) {
        console.error("Error fetching master data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMasterData();
  }, [tenantId]);

  // 2. Fetch specific Start/End dates whenever the active Academic Year changes
  useEffect(() => {
    const fetchSpecificYearDates = async () => {
      if (!tenantId || !academicYear) return;
      
      try {
        const calendarRef = doc(db, 'districts', tenantId, 'settings', `calendar-${academicYear}`);
        const calendarSnap = await getDoc(calendarRef);
        
        if (calendarSnap.exists()) {
          const calData = calendarSnap.data();
          setStartDate(calData.startDate || '');
          setEndDate(calData.endDate || '');
        } else {
          setStartDate('');
          setEndDate('');
        }
      } catch (error) {
        console.error("Error fetching specific year dates:", error);
      }
    };

    fetchSpecificYearDates();
  }, [tenantId, academicYear]);

  // --- EXISTING HANDLERS ---
  const handleCreateNewYear = async (e) => {
    e.preventDefault();
    if (!newYearInput) return;

    try {
      const districtRef = doc(db, 'districts', tenantId);
      await setDoc(districtRef, {
        configuredYears: arrayUnion(newYearInput)
      }, { merge: true });
      
      setAvailableYears([...availableYears, newYearInput]);
      setAcademicYear(newYearInput); 
      setNewYearInput('');
      setIsAddingYear(false);
    } catch (error) {
      console.error("Error adding new year:", error);
    }
  };

  const handleAddHoliday = (e) => {
    e.preventDefault();
    if (selectedHoliday && !holidays.includes(selectedHoliday)) {
      setHolidays([...holidays, selectedHoliday].sort());
      setSelectedHoliday(''); 
    }
  };

  const handleRemoveHoliday = (dateToRemove) => {
    setHolidays(holidays.filter(date => date !== dateToRemove));
  };

  // --- NEW SPRINT HANDLERS ---
  const formatDateString = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T12:00:00'); // Timezone fix
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleAddSprint = (e) => {
    e.preventDefault();
    if (!sprintStart || !sprintEnd) return;

    const sprintString = `Sprint ${sprintNum}: ${formatDateString(sprintStart)} - ${formatDateString(sprintEnd)}`;

    setSprints([...sprints, sprintString]);
    setSprintNum(sprintNum + 1);
    setSprintStart('');
    setSprintEnd('');
    
    if (sprints.length === 0) setActiveSprint(sprintString);
  };

  const handleRemoveSprint = (sprintToRemove) => {
    setSprints(sprints.filter(s => s !== sprintToRemove));
    if (activeSprint === sprintToRemove) setActiveSprint('');
  };

  // --- COMBINED SAVE SYSTEM ---
  const handleSaveCalendar = async () => {
    setSaveStatus('Saving district configuration...');
    try {
      // 1. Save Holidays to District Root
      const districtRef = doc(db, 'districts', tenantId);
      await setDoc(districtRef, { nonInstructionalDays: holidays }, { merge: true });
      
      // 2. Save Start/End Dates to Specific Year File
      const calendarRef = doc(db, 'districts', tenantId, 'settings', `calendar-${academicYear}`);
      await setDoc(calendarRef, {
        academicYear,
        startDate,
        endDate,
        updatedAt: new Date().toISOString()
      }, { merge: true });

      // 3. Save Sprints to Master Settings File (This controls the Gen Ed App!)
      const sprintRef = doc(db, 'districts', tenantId, 'settings', 'calendar');
      await setDoc(sprintRef, {
        sprints: sprints,
        activeSprint: activeSprint,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      
      setSaveStatus('Calendar & Sprints Updated Successfully!');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error("Error saving calendar: ", error);
      setSaveStatus('Error saving configuration.');
    }
  };

  return (
    <div style={{ padding: '30px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', maxWidth: '650px', margin: '0 auto', fontFamily: 'sans-serif' }}>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: '2px solid #eee', paddingBottom: '10px', marginBottom: '25px' }}>
        <div>
          <h2 style={{ color: '#003366', marginTop: 0, marginBottom: '5px' }}>God Mode: District Calendar</h2>
          <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>Define instructional boundaries and reporting periods.</p>
        </div>
        {loading && <span style={{ color: '#999', fontSize: '13px', fontStyle: 'italic' }}>Loading data...</span>}
      </div>

      {/* Year Manager Dropdown */}
      <div style={{ backgroundColor: '#f0f5fa', padding: '15px', borderRadius: '6px', marginBottom: '25px', border: '1px solid #cce0ff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <label style={{ fontWeight: 'bold', fontSize: '14px', color: '#003366' }}>Active Academic Year</label>
          {!isAddingYear && (
            <button onClick={() => setIsAddingYear(true)} style={{ background: 'none', border: 'none', color: '#0055cc', fontSize: '13px', cursor: 'pointer', fontWeight: 'bold' }}>
              + Initialize New Year
            </button>
          )}
        </div>

        {isAddingYear ? (
          <form onSubmit={handleCreateNewYear} style={{ display: 'flex', gap: '10px' }}>
            <input type="text" placeholder="e.g., 2027-2028" value={newYearInput} onChange={(e) => setNewYearInput(e.target.value)} style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
            <button type="submit" style={{ padding: '8px 15px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Add</button>
            <button type="button" onClick={() => setIsAddingYear(false)} style={{ padding: '8px 15px', backgroundColor: '#666', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
          </form>
        ) : (
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '16px', fontWeight: 'bold' }}>
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        )}
      </div>

      {/* Basic Settings */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '25px' }}>
        <div>
          <label style={{ display: 'block', fontWeight: 'bold', fontSize: '13px', marginBottom: '5px' }}>First Day of School ({academicYear})</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: 'bold', fontSize: '13px', marginBottom: '5px' }}>Last Day of School ({academicYear})</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
        </div>
      </div>

      {/* Holiday Builder */}
      <div style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '6px', border: '1px solid #eee', marginBottom: '30px' }}>
        <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#333' }}>No School Days (Compliance Engine)</h3>
        
        <form onSubmit={handleAddHoliday} style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
          <input type="date" value={selectedHoliday} onChange={(e) => setSelectedHoliday(e.target.value)} style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
          <button type="submit" style={{ padding: '8px 15px', backgroundColor: '#003366', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Add Date</button>
        </form>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {holidays.length === 0 && <span style={{ color: '#999', fontSize: '13px' }}>No dates added yet.</span>}
          {holidays.map((date) => (
            <div key={date} style={{ backgroundColor: '#e6f0fa', color: '#003366', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              {date}
              <button type="button" onClick={() => handleRemoveHoliday(date)} style={{ background: 'none', border: 'none', color: '#ba0c2f', cursor: 'pointer', fontWeight: 'bold', padding: 0 }}>×</button>
            </div>
          ))}
        </div>
      </div>

      {/* --- NEW: THE SPRINT BUILDER --- */}
      <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', border: '2px solid #003366', marginBottom: '30px' }}>
        <h3 style={{ margin: '0 0 5px 0', fontSize: '18px', color: '#003366' }}>Data Collection Sprints</h3>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '20px', marginTop: 0 }}>Define the bi-weekly behavior logging periods for Gen Ed teachers.</p>

        {/* Generate Sprint Form */}
        <form onSubmit={handleAddSprint} style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '20px', backgroundColor: '#f4f7f6', padding: '15px', borderRadius: '6px' }}>
          <div style={{ width: '60px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#666', marginBottom: '3px' }}>Sprint #</label>
            <input type="number" value={sprintNum} onChange={(e) => setSprintNum(Number(e.target.value))} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#666', marginBottom: '3px' }}>Start</label>
            <input type="date" value={sprintStart} onChange={(e) => setSprintStart(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#666', marginBottom: '3px' }}>End</label>
            <input type="date" value={sprintEnd} onChange={(e) => setSprintEnd(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }} />
          </div>
          <button type="submit" style={{ padding: '9px 15px', backgroundColor: '#003366', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Add</button>
        </form>

        {/* Active Sprints List */}
        {sprints.length > 0 && (
          <div style={{ borderTop: '1px solid #eee', paddingTop: '15px' }}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#28a745', marginBottom: '5px' }}>
                Currently Active Sprint (Gen Ed Default View):
              </label>
              <select 
                value={activeSprint} 
                onChange={(e) => setActiveSprint(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '2px solid #28a745', fontWeight: 'bold', backgroundColor: '#f4fff4' }}
              >
                <option value="">-- Select Active Sprint --</option>
                {sprints.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {sprints.map((sprint, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', backgroundColor: '#f9f9f9', border: '1px solid #eee', borderRadius: '4px' }}>
                  <span style={{ fontSize: '14px', color: '#333' }}>{sprint}</span>
                  <button type="button" onClick={() => handleRemoveSprint(sprint)} style={{ background: 'none', border: 'none', color: '#ba0c2f', cursor: 'pointer', fontWeight: 'bold' }}>Remove</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: saveStatus.includes('Error') ? '#ba0c2f' : '#28a745', fontWeight: 'bold', fontSize: '14px' }}>{saveStatus}</span>
        <button onClick={handleSaveCalendar} style={{ padding: '12px 24px', backgroundColor: '#ba0c2f', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', boxShadow: '0 4px 6px rgba(186, 12, 47, 0.2)' }}>
          Publish District Changes
        </button>
      </div>

    </div>
  );
};

export default GodModeCalendar;